import { forwardRef } from 'react';
import {
	getButtonClassNames,
	cn,
	getLabelWrapperClassName,
	outlineCtaOuterClassNames,
	outlineCtaInnerClassNames,
} from './buttonStyles';
import { ButtonSpinner } from './ButtonSpinner';

/**
 * Contenido del botón con icono opcional a la izquierda o derecha del texto.
 * @param {{ children: import('react').ReactNode; icon?: import('react').ReactNode; iconPosition?: 'start' | 'end'; labelCase?: 'none' | 'sentence' | 'uppercase' }} p
 */
export const renderButtonInnerContent = ({
	children,
	icon = null,
	iconPosition = 'start',
	labelCase = 'sentence',
}) => {
	const wrapClass = getLabelWrapperClassName(labelCase);
	const labelBody = wrapClass ? <span className={wrapClass}>{children}</span> : children;
	if (!icon) return labelBody;
	const iconEl = (
		<span className="inline-flex shrink-0 items-center justify-center" aria-hidden>
			{icon}
		</span>
	);
	if (iconPosition === 'end') {
		return (
			<>
				{labelBody}
				{iconEl}
			</>
		);
	}
	return (
		<>
			{iconEl}
			{labelBody}
		</>
	);
};

/**
 * @typedef {'primary' | 'secondary' | 'outline' | 'danger' | 'dangerGradient' | 'success' | 'warning' | 'ghost' | 'gradient' | 'outlineCta' | 'drawerActions' | 'barFilterSecondary' | 'barFilterPrimary'} ButtonVariant
 * @typedef {'sm' | 'md' | 'lg'} ButtonSize
 * @typedef {'none' | 'sentence' | 'uppercase'} ButtonLabelCase
 */

export const Button = forwardRef(
	(
		{
			children,
			icon = null,
			iconPosition = 'start',
			className = '',
			variant = 'primary',
			size = 'md',
			fullWidth = false,
			loading = false,
			loadingLabel = '',
			labelCase = 'sentence',
			disabled = false,
			type = 'button',
			...rest
		},
		ref,
	) => {
		const rootClass =
			variant === 'outlineCta'
				? cn(outlineCtaOuterClassNames, className)
				: cn(getButtonClassNames({ variant, size, fullWidth }), className);

		const inner = renderButtonInnerContent({ children, icon, iconPosition, labelCase });

		return (
			<button
				ref={ref}
				type={type}
				disabled={disabled || loading}
				className={rootClass}
				{...rest}
			>
				{variant === 'gradient' ? (
					<>
						<span className="relative z-10 inline-flex items-center justify-center gap-2">
							{loading ? (
								<ButtonSpinner label={loadingLabel} labelCase={labelCase} />
							) : (
								inner
							)}
						</span>
						{!loading ? (
							<div
								className="pointer-events-none absolute inset-0 bg-gradient-to-r from-purple-600 to-indigo-600 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
								aria-hidden
							/>
						) : null}
					</>
				) : variant === 'outlineCta' ? (
					<span className={outlineCtaInnerClassNames}>
						{loading ? (
							<ButtonSpinner label={loadingLabel} labelCase={labelCase} />
						) : (
							inner
						)}
					</span>
				) : loading ? (
					<ButtonSpinner label={loadingLabel} labelCase={labelCase} />
				) : (
					inner
				)}
			</button>
		);
	},
);

Button.displayName = 'Button';
